'use client';

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import type { GameRecipe } from '@/app/lib/gameData/types';
import { getItemTexturePath, getBlockTexturePath, isIconlessId, translucentIconStyle } from '@/app/lib/minecraft';
import { useVersion } from '@/app/context/VersionContext';
import { trackIdListCopy } from '@/app/lib/trackStats';
import { useTrackToolView } from '@/app/hooks/useTrackToolView';
import { loadVersionCategory, hasVersionCategory } from '@/app/lib/gameData/loader';
import { resolveDataVersion, getDefaultVersion } from '@/app/lib/versions';
import { buildRecipeIndexes } from '@/app/tools/recipe-viewer/lib/recipeIndex';
import { getModByNamespace, getModpackById, loadTags, type TagIndex } from '@/app/lib/gameData/modRegistry';
import GameVersionModSelector from '@/app/components/ui/GameVersionModSelector';
import ExperimentalBanner from '@/app/components/ui/ExperimentalBanner';
import { useAuth } from '@/app/context/AuthContext';
import { useFavorites } from '@/app/hooks/useFavorites';
import AuthPromptModal from '@/app/components/ui/AuthPromptModal';
import { SECONDARY_BUTTON } from '@/app/components/ui/actionButtons';

import { buildIdListData, rowsForType, type DisplayItem } from './lib/idListRows';
import {
  DATA_TYPES,
  ITEMS_PER_PAGE,
  TAB_HEADINGS,
  TAB_LABELS,
  idListHref,
  pageWindow,
  parseIdListQuery,
  type IdListQuery,
} from './lib/idListUrl';

type SourceFilter = 'all' | 'vanilla' | 'modded';
type SortField = 'stringId' | 'displayName';
type SortDirection = 'asc' | 'desc';

/** Locale-safe number formatter that avoids SSR/client hydration mismatches */
const fmtNum = (n: number) => n.toLocaleString('en-US');

// Track textures that have already 404'd to avoid repeated requests on re-render
const failedTextures = new Set<string>();

export default function IdListClient() {
  useTrackToolView('id-list');
  const { data: versionData, version, edition, modSelection } = useVersion();
  const { user } = useAuth();
  const { isFavorited, toggleFavorite } = useFavorites();
  const [hasMounted, setHasMounted] = useState(false);

  // 🚨 Tab, page and search live in the URL, not in state — see lib/idListUrl.ts for why (crawlers
  // could read 75 of 1,882 IDs). The server renders whatever the URL asks for, and every control below
  // is a real link to it.
  const searchParams = useSearchParams();
  const urlQuery = useMemo(() => parseIdListQuery(searchParams), [searchParams]);
  const dataType = urlQuery.type;
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  // Search is typed, so it is local state that WRITES the URL (replaceState) rather than being read
  // back from it on every keystroke — a controlled input fed by an async router update drops keys.
  // 🚨 The box is NEVER re-derived from useSearchParams while it is in use — only on mount (above) and
  // on Back/Forward (below). Both of the earlier designs lost typed characters, measured with a real
  // browser typing at 5–40 ms per key:
  //  1. Syncing whenever the URL's search differed from our last write: Next applies each
  //     history.replaceState as a background (startTransition) render, so a render for an OLDER
  //     keystroke commits after newer ones and looked like an outside navigation.
  //  2. Also requiring it to match the live address bar: Next's router then REWRITES the address bar
  //     to that stale render's URL when it commits, so the stale value matched too ("deepk").
  // popstate fires only for a real Back/Forward, never for replaceState or a router commit.
  // (Arriving from another page — e.g. the Saved library's favorite link — mounts this component, so
  // the initial state above already reads that search.)
  const [searchQuery, setSearchQuery] = useState(urlQuery.search);
  useEffect(() => {
    const onPopState = () => {
      setSearchQuery((new URLSearchParams(window.location.search).get('search') ?? '').slice(0, 100));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const [sortField, setSortField] = useState<SortField>('displayName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  // Defer auth-dependent UI to avoid hydration mismatch
  useEffect(() => setHasMounted(true), []);
  const showFavorites = hasMounted;
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Load recipes for detail panel
  const [recipes, setRecipes] = useState<Record<string, GameRecipe>>({});
  const resolvedRecipeVersion = useMemo(() => {
    const dataVersion = resolveDataVersion(version, edition);
    if (hasVersionCategory(dataVersion, 'recipes', edition)) return dataVersion;
    // Was hardcoded '1.21.11'. A literal here silently rots: the default moved to 26.2 and this
    // kept serving 1.21.11 recipes to anyone on a version with no recipe dump of its own. Read
    // the registry instead so it tracks the default automatically. (Same trap the version-format
    // rule flags for generator fallbacks.)
    return getDefaultVersion(edition);
  }, [version, edition]);

  useEffect(() => {
    let cancelled = false;
    loadVersionCategory(resolvedRecipeVersion, 'recipes', edition).then(data => {
      if (!cancelled) setRecipes((data as Record<string, GameRecipe>) ?? {});
    }).catch(() => {
      if (!cancelled) setRecipes({});
    });
    return () => { cancelled = true; };
  }, [resolvedRecipeVersion, edition]);

  // Load tags lazily for detail panel
  const [tagIndex, setTagIndex] = useState<TagIndex | null>(null);
  const tagLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    const modpackPath = modSelection?.modpack
      ? getModpackById(modSelection.modpack)?.dataPath || null
      : null;
    const tagKey = modpackPath || `vanilla-${version}`;

    if (tagLoadedRef.current === tagKey) return;
    tagLoadedRef.current = tagKey;

    loadTags(modpackPath ? { modpackDataPath: modpackPath } : { vanillaVersion: version })
      .then(t => setTagIndex(t))
      .catch(() => setTagIndex(null));
  }, [version, modSelection]);

  /** Get tags for an item/block/entity by stringId */
  const getItemTags = useCallback((stringId: string, type: 'item' | 'block' | 'entity'): string[] => {
    if (!tagIndex) return [];
    const category = type === 'entity' ? 'entities' : type === 'block' ? 'blocks' : 'items';
    return tagIndex[category]?.[stringId] || [];
  }, [tagIndex]);

  // Pre-build recipe indexes when recipes data is available
  const recipeIndexes = useMemo(() => {
    if (Object.keys(recipes).length === 0) return null;
    const allItemIds = new Set(versionData.items.map(i => i.stringId));
    return buildRecipeIndexes(recipes, allItemIds);
  }, [recipes, versionData.items]);

  // Convert all data to unified format — lib/idListRows.ts, shared with the server's page counts.
  const { allData, overriddenIds, hasModdedContent, availableMods } = useMemo(
    () => buildIdListData(versionData),
    [versionData],
  );

  // Parse search query — supports @ModName prefix to filter by mod
  // e.g. "@Create" shows all Create items, "@Create door" shows Create items matching "door"
  const parsedSearch = useMemo(() => {
    const query = searchQuery.trim();
    if (!query.startsWith('@')) {
      return { modFilter: null, textQuery: query };
    }

    const spaceIndex = query.indexOf(' ', 1);
    if (spaceIndex === -1) {
      // Just "@something" with no text query after
      return { modFilter: query.slice(1), textQuery: '' };
    }

    return {
      modFilter: query.slice(1, spaceIndex),
      textQuery: query.slice(spaceIndex + 1).trim(),
    };
  }, [searchQuery]);

  // Resolve @modFilter to matched namespace(s) — partial, case-insensitive match
  const matchedModNamespaces = useMemo(() => {
    if (!parsedSearch.modFilter) return null;
    const filter = parsedSearch.modFilter.toLowerCase();
    const matched = new Set<string>();
    for (const [namespace, displayName] of availableMods) {
      if (namespace.toLowerCase().includes(filter) || displayName.toLowerCase().includes(filter)) {
        matched.add(namespace);
      }
    }
    return matched;
  }, [parsedSearch.modFilter, availableMods]);

  // Get data based on selected type
  const rawData = useMemo(() => rowsForType(allData, dataType), [dataType, allData]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = rawData;

    // Source filter (All / Vanilla / Modded)
    if (sourceFilter === 'vanilla') {
      data = data.filter(item => !item.namespace || item.namespace === 'minecraft');
    } else if (sourceFilter === 'modded') {
      data = data.filter(item => item.namespace && item.namespace !== 'minecraft');
    }

    // @mod filter from search query
    if (matchedModNamespaces) {
      data = data.filter(item => item.namespace && matchedModNamespaces.has(item.namespace));
    }

    // Text search (either the full query or the part after @ModName)
    const textQuery = parsedSearch.textQuery || (parsedSearch.modFilter ? '' : searchQuery);
    if (textQuery) {
      const query = textQuery.toLowerCase();
      data = data.filter(item =>
        item.stringId.toLowerCase().includes(query) ||
        item.displayName.toLowerCase().includes(query)
      );
    }

    // Sort — filter() already creates new arrays, but if no filters applied we need a copy
    if (data === rawData) data = [...data];
    data.sort((a, b) => {
      let aVal: string | number | null = a[sortField] ?? null;
      let bVal: string | number | null = b[sortField] ?? null;

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal === null) aVal = '';
      if (bVal === null) bVal = '';

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [rawData, sourceFilter, matchedModNamespaces, parsedSearch, searchQuery, sortField, sortDirection]);

  // The page comes from the URL and is CLAMPED, never reset by an effect. The old
  // `useEffect(() => setPage(1), [..., version])` also ran on MOUNT — and again when VersionContext
  // restored a saved version after hydration — so it silently threw away every `?page=` link.
  // The row count can still change under the page (a smaller version loads, a search narrows it);
  // clamping shows the last real page instead.
  const totalPages = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));
  const page = Math.min(urlQuery.page, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Moves between tabs and pages WITHOUT a server round trip: Next syncs history.pushState into
  // useSearchParams (docs: linking-and-navigating), so the table re-renders from the new URL while
  // every control stays a real href a crawler can follow. Deliberately not <Link>, which prefetches
  // each visible link — ~10 per page view — and requests are most of the hosting bill.
  const writeUrl = useCallback((next: IdListQuery, mode: 'push' | 'replace') => {
    const href = idListHref(next);
    if (mode === 'push') window.history.pushState(null, '', href);
    else window.history.replaceState(null, '', href);
  }, []);

  const followLink = (e: React.MouseEvent<HTMLAnchorElement>, next: IdListQuery) => {
    // New tab / new window / download clicks keep the browser's own behavior.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    // Clicking where you already are must not add a Back entry that goes nowhere.
    if (idListHref(next) === idListHref({ type: dataType, page, search: searchQuery })) return;
    writeUrl(next, 'push');
    setExpandedId(null);
  };

  const followPageLink = (e: React.MouseEvent<HTMLAnchorElement>, next: IdListQuery) => {
    followLink(e, next);
    if (e.defaultPrevented) tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** A change that makes the current page meaningless (filter, sort) goes back to page 1. */
  const resetPage = () => {
    if (urlQuery.page !== 1) writeUrl({ type: dataType, page: 1, search: searchQuery }, 'replace');
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    writeUrl({ type: dataType, page: 1, search: value }, 'replace');
  };

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    resetPage();
  };

  // The identifier pill and its copy button. Rendered in the Identifier column from `sm` up and
  // under the name below it — at a phone width a separate column squeezed the names to three lines
  // and pushed Type and the favorite star off the right edge of the table.
  const renderIdPill = (item: DisplayItem) => (
    <div className="flex items-center gap-2">
      {/* No max-width or truncate: the ID is the thing people copy, and a 200px cap cut it
          with "…" while the column had room to spare. On a narrow column it wraps instead. */}
      <code className="min-w-0 break-all text-xs text-cyan-400 font-mono bg-zinc-800 px-2 py-1 rounded">
        {item.namespace && item.namespace !== 'minecraft' ? item.fullId : item.stringId}
      </code>
      <button
        onClick={(e) => {
          e.stopPropagation();
          const idToCopy = item.namespace && item.namespace !== 'minecraft' ? item.fullId : item.stringId;
          copyToClipboard(idToCopy, item.fullId);
        }}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          copiedId === item.fullId
            ? 'text-emerald-400 bg-emerald-500/20'
            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
        }`}
        title={copiedId === item.fullId ? 'Copied!' : 'Copy ID'}
      >
        {copiedId === item.fullId ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </div>
  );

  const navButton = `px-4 py-2 text-sm font-medium rounded-lg hover:text-white ${SECONDARY_BUTTON}`;
  const renderPagination = (edge: 'top' | 'bottom') => {
    if (totalPages <= 1) return null;
    const prev = { type: dataType, page: page - 1, search: searchQuery };
    const next = { type: dataType, page: page + 1, search: searchQuery };
    // Two number windows: ±2 pages from `sm` up, ±1 below it. Seven 40px numbers plus Previous and
    // Next need ~440px, so on a phone the row broke into four ragged lines (Previous / numbers /
    // an orphaned "… 16" / Next). Below `sm` the control is instead two tidy lines — Previous and
    // Next on the first, a ±1 window (at most 5 numbers + 2 gaps; 32px numbers keep it to ~220px, which a 320 viewport has — 36px wrapped the last number)
    // centered on the second. The hidden window is display:none, so it is out of the a11y tree.
    const renderWindow = (radius: number, className: string) => (
      <div className={className}>
        {pageWindow(page, totalPages, radius).map((p, i) => {
          if (p === null) return <span key={`gap-${i}`} className="px-1 text-zinc-600" aria-hidden="true">…</span>;
          if (p === page) {
            return (
              <span key={p} aria-current="page" className="min-w-8 sm:min-w-10 min-h-10 px-1.5 sm:px-2 inline-flex items-center justify-center rounded-md bg-emerald-600 text-emerald-950 font-medium">
                {fmtNum(p)}
              </span>
            );
          }
          const target = { type: dataType, page: p, search: searchQuery };
          return (
            <a
              key={p}
              href={idListHref(target)}
              onClick={e => followPageLink(e, target)}
              aria-label={`Page ${p}`}
              // 40px-tall targets: the page numbers are the main way through the list on a phone.
              className="min-w-8 sm:min-w-10 min-h-10 px-1.5 sm:px-2 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              {fmtNum(p)}
            </a>
          );
        })}
      </div>
    );
    return (
      <nav
        aria-label={edge === 'top' ? 'Pages' : 'Pages, bottom'}
        className={`p-4 ${edge === 'top' ? 'border-b' : 'border-t'} border-zinc-800 grid grid-cols-2 items-center gap-3 sm:flex sm:flex-wrap sm:justify-between`}
      >
        {page > 1 ? (
          <a href={idListHref(prev)} onClick={e => followPageLink(e, prev)} rel="prev" className={`${navButton} justify-self-start`}>Previous</a>
        ) : (
          <span role="link" aria-disabled="true" className={`${navButton} justify-self-start opacity-40 cursor-not-allowed`}>Previous</span>
        )}
        {renderWindow(1, 'col-span-2 order-last flex flex-wrap items-center justify-center gap-1 text-sm sm:hidden')}
        {renderWindow(2, 'hidden sm:flex flex-wrap items-center justify-center gap-1 text-sm')}
        {page < totalPages ? (
          <a href={idListHref(next)} onClick={e => followPageLink(e, next)} rel="next" className={`${navButton} justify-self-end`}>Next</a>
        ) : (
          <span role="link" aria-disabled="true" className={`${navButton} justify-self-end opacity-40 cursor-not-allowed`}>Next</span>
        )}
      </nav>
    );
  };

  // Get texture path — use item's own texturePath first (covers mod items),
  // fall back to vanilla lookup functions. Returns '' if previously failed.
  const getTexture = (item: DisplayItem): string => {
    if (item.dataType === 'entity') {
      return '';
    }
    // Air & co. have no icon — show the empty-slot placeholder, don't request an image.
    // Checked on fullId so a modded `<mod>:air` in a modpack keeps its own icon.
    if (isIconlessId(item.fullId)) {
      return '';
    }
    let path = item.texturePath || '';
    if (!path) {
      path = item.dataType === 'block'
        ? getBlockTexturePath(item.stringId)
        : getItemTexturePath(item.stringId);
    }
    return failedTextures.has(path) ? '' : path;
  };

  // Sort indicator — uses module-level SortIndicator component


  // Copy to clipboard
  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      trackIdListCopy({
        copyType: text.startsWith('/') ? 'command' : 'identifier',
      });
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get data type label color
  const getTypeColor = (type: 'item' | 'block' | 'entity' | 'enchantment' | 'effect') => {
    switch (type) {
      case 'item': return 'bg-emerald-500/20 text-emerald-400';
      case 'block': return 'bg-blue-500/20 text-blue-400';
      case 'entity': return 'bg-purple-500/20 text-purple-400';
      case 'enchantment': return 'bg-violet-500/20 text-violet-400';
      case 'effect': return 'bg-amber-500/20 text-amber-400';
    }
  };

  // CopyBtn and DetailRow are module-level components below

  // Get recipe summary for an item (uses pre-built indexes from useMemo)
  const getRecipeSummary = (stringId: string) => {
    const indexes = recipeIndexes;
    if (!indexes) return { obtainCount: 0, usedInCount: 0 };
    return {
      obtainCount: indexes.forward.get(stringId)?.length ?? 0,
      usedInCount: indexes.reverse.get(stringId)?.length ?? 0,
    };
  };

  // Format recipe type for display
  const formatRecipeType = (type: string) => {
    switch (type) {
      case 'crafting': return 'Crafting';
      case 'smelting': return 'Smelting';
      case 'blasting': return 'Blasting';
      case 'smoking': return 'Smoking';
      case 'campfire_cooking': return 'Campfire';
      case 'stonecutting': return 'Stonecutting';
      case 'smithing': return 'Smithing';
      default: return type;
    }
  };

  // Get rarity color
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'uncommon': return 'text-yellow-400';
      case 'rare': return 'text-cyan-400';
      case 'epic': return 'text-purple-400';
      default: return 'text-zinc-400';
    }
  };

  // Get mod links from registry (id = Modrinth slug)
  const getModLinks = (namespace: string) => {
    const mod = getModByNamespace(namespace);
    if (!mod) return null;
    return {
      modrinth: `https://modrinth.com/mod/${mod.id}`,
      curseforge: `https://www.curseforge.com/minecraft/mc-mods/${mod.id}`,
    };
  };

  return (
    <div className="p-6 pt-8">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-3">
              <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
              {TAB_HEADINGS[dataType]}
            </h1>
            <div className="max-lg:fixed max-lg:top-[calc(0.75rem+env(safe-area-inset-top))] max-lg:right-3 max-lg:z-30">
              <GameVersionModSelector dataOnly />
            </div>
          </div>
          <p className="text-sm text-zinc-400 mt-1 ml-10">
            Complete database of all Minecraft items, blocks, mobs, enchantments, and effects with their IDs
          </p>
        </div>

        <ExperimentalBanner />

        {overriddenIds.size > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-300">Mod Overrides Detected</h3>
                <p className="text-sm text-blue-300/70 mt-1">
                  {overriddenIds.size} item{overriddenIds.size !== 1 ? 's share' : ' shares'} an ID with vanilla Minecraft and {overriddenIds.size !== 1 ? 'have' : 'has'} been modified by a mod.
                  Both versions are shown in the list.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Data Type Tabs */}
            {/* Wraps rather than scrolling: a scrolled strip cut "Effects" to "Effe" with no hint
                that more tabs existed. */}
            <nav aria-label="ID type" className="flex flex-wrap gap-y-1 bg-zinc-800 rounded-lg p-1">
              {DATA_TYPES.map(key => {
                const target = { type: key, page: 1, search: searchQuery };
                return (
                  <a
                    key={key}
                    href={idListHref(target)}
                    onClick={e => followLink(e, target)}
                    aria-current={dataType === key ? 'page' : undefined}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      dataType === key
                        ? 'bg-emerald-600 text-emerald-950'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                    }`}
                  >
                    {TAB_LABELS[key]}
                  </a>
                );
              })}
            </nav>

            {/* Source Filter Tabs — only shown when mods are loaded */}
            {hasModdedContent && (
              <div className="flex flex-wrap bg-zinc-800 rounded-lg p-1 whitespace-nowrap">
                {([
                  { key: 'all', label: 'All' },
                  { key: 'vanilla', label: 'Vanilla' },
                  { key: 'modded', label: 'Modded' },
                ] as { key: SourceFilter; label: string }[]).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setSourceFilter(tab.key); resetPage(); }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      sourceFilter === tab.key
                        ? 'bg-purple-600 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  // The URL keeps 100 characters (parseIdListQuery); a longer box would reload differently.
                  maxLength={100}
                  placeholder={hasModdedContent ? 'Search... or @ModName to filter by mod' : 'Search by name or identifier...'}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

          </div>

          {/* Active @mod filter indicator */}
          {matchedModNamespaces && matchedModNamespaces.size > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-zinc-500">Filtering by:</span>
              {[...matchedModNamespaces].map(ns => (
                <span key={ns} className="text-xs font-medium bg-purple-500/15 text-purple-400 px-2 py-0.5 rounded-full">
                  {availableMods.get(ns) || ns}
                </span>
              ))}
              {parsedSearch.textQuery && (
                <span className="text-xs text-zinc-500">
                  matching &quot;{parsedSearch.textQuery}&quot;
                </span>
              )}
            </div>
          )}
          {parsedSearch.modFilter && matchedModNamespaces && matchedModNamespaces.size === 0 && (
            <div className="mt-3 text-xs text-amber-400/70">
              No mods matching &quot;{parsedSearch.modFilter}&quot; found. Available: {[...availableMods.values()].join(', ') || 'none loaded'}
            </div>
          )}

          {/* Stats */}
          <div className="mt-4 flex items-center gap-4 text-sm text-zinc-500">
            <span>
              Showing <span className="text-white font-medium">{fmtNum(filteredData.length > 0 ? (page - 1) * ITEMS_PER_PAGE + 1 : 0)}&ndash;{fmtNum(Math.min(page * ITEMS_PER_PAGE, filteredData.length))}</span> of <span className="text-white font-medium">{fmtNum(filteredData.length)}</span> matching entries
              {filteredData.length !== rawData.length && (
                <span className="text-zinc-500"> ({fmtNum(rawData.length)} total)</span>
              )}
            </span>
          </div>
        </div>

        {/* Data Table */}
        <div ref={tableRef} className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          {renderPagination('top')}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-800/50 border-b border-zinc-700">
                  {/* No `uppercase` on these headers: the sortable ones already rendered title case
                      (their text sits in a <button>), so only "TYPE" was shouting next to "Identifier".
                      The 45% width is `sm:` only — below sm the Identifier column is hidden, and a
                      fixed 45% handed the rest to the star column and wrapped IDs one letter a line. */}
                  <th className="px-3 py-3 text-left text-xs font-medium text-zinc-400 sm:w-[45%]">
                    <div className="flex items-center">
                      <span className="w-6 shrink-0 mr-2"></span>
                      <span className="w-8 shrink-0 mr-3"></span>
                      <button className="hover:text-white transition-colors" onClick={() => handleSort('displayName')}>
                        In-Game Name <SortIndicator field="displayName" sortField={sortField} sortDirection={sortDirection} />
                      </button>
                      {/* Below sm the Identifier column (and its sort button) is hidden, so sorting by ID
                          would be unreachable on a phone without this. */}
                      <button className="sm:hidden ml-4 hover:text-white transition-colors" onClick={() => handleSort('stringId')}>
                        ID <SortIndicator field="stringId" sortField={sortField} sortDirection={sortDirection} />
                      </button>
                    </div>
                  </th>
                  <th className="hidden sm:table-cell px-3 py-3 text-left text-xs font-medium text-zinc-400">
                    <button className="hover:text-white transition-colors" onClick={() => handleSort('stringId')}>
                      Identifier <SortIndicator field="stringId" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  {dataType === 'all' && (
                    <th className="hidden sm:table-cell px-3 py-3 text-right text-xs font-medium text-zinc-400 w-20">
                      Type
                    </th>
                  )}
                  {showFavorites && (
                    <th className="px-3 py-3 text-center text-xs font-medium text-zinc-400 w-12">
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {paginatedItems.map((item, index) => {
                  const texture = (item.dataType !== 'entity' && item.dataType !== 'enchantment' && item.dataType !== 'effect') ? getTexture(item) : '';
                  const isExpanded = expandedId === `${item.fullId}-${item.dataType}`;
                  const rowKey = `${item.fullId}-${item.dataType}`;
                  return (
                  <React.Fragment key={rowKey}>
                  <tr
                    className={`transition-colors cursor-pointer ${
                      isExpanded ? 'bg-zinc-800/70' : index % 2 === 0 ? 'bg-zinc-900 hover:bg-zinc-800/50' : 'bg-zinc-900/50 hover:bg-zinc-800/50'
                    }`}
                    onClick={() => setExpandedId(isExpanded ? null : rowKey)}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center">
                        {/* Expand chevron */}
                        <svg className={`w-4 h-4 text-zinc-500 mr-2 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {/* Icon */}
                        <div className="w-8 h-8 bg-zinc-800 rounded flex items-center justify-center overflow-hidden shrink-0">
                          {texture ? (
                            <Image
                              src={texture}
                              alt={item.displayName}
                              width={32}
                              height={32}
                              className="pixelated"
                              style={translucentIconStyle(texture)}
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                failedTextures.add(texture);
                                if (img.dataset.fallbackTried !== '1' && item.dataType === 'item' && !img.src.includes('/block/')) {
                                  const fallback = getBlockTexturePath(item.stringId);
                                  if (!failedTextures.has(fallback)) {
                                    img.dataset.fallbackTried = '1';
                                    failedTextures.add(fallback);
                                    img.src = fallback;
                                    return;
                                  }
                                }
                                img.style.display = 'none';
                              }}
                              unoptimized
                            />
                          ) : item.dataType === 'entity' ? (
                            <span className="text-purple-400 text-xs">MOB</span>
                          ) : item.dataType === 'enchantment' ? (
                            <span className="text-violet-400 text-lg">{"\u2728"}</span>
                          ) : item.dataType === 'effect' ? (
                            item.effectColor ? (
                              <div className="w-5 h-5 rounded-full" style={{ backgroundColor: item.effectColor }} />
                            ) : (
                              <span className="text-amber-400 text-lg">{"\u2B50"}</span>
                            )
                          ) : isIconlessId(item.fullId) ? (
                            // Air & co. have no item form, so no sprite exists: a deliberate EMPTY
                            // SLOT (dashed outline) rather than "N/A" text or the dump's noise PNG.
                            <svg className="w-5 h-5 text-zinc-500" viewBox="0 0 20 20" fill="none" role="img" aria-label="No icon">
                              <title>{`${item.displayName} has no item icon`}</title>
                              <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2.5" />
                            </svg>
                          ) : (
                            <span className="text-zinc-500 text-xs">N/A</span>
                          )}
                        </div>
                        {/* Name (+ identifier and type underneath it below `sm`) */}
                        <div className="ml-3 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-white text-sm">{item.displayName}</span>
                          {item.namespace && item.namespace !== 'minecraft' && (
                            <span className="text-[10px] font-medium bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                              {item.modDisplayName || item.namespace}
                            </span>
                          )}
                          {item.namespace && item.namespace !== 'minecraft' && overriddenIds.has(item.stringId) && (
                            <span
                              className="text-[10px] font-medium bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full whitespace-nowrap"
                              title={`This mod replaces vanilla minecraft:${item.stringId}`}
                            >
                              Overrides
                            </span>
                          )}
                        </div>
                        <div className="sm:hidden mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                          {renderIdPill(item)}
                          {dataType === 'all' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getTypeColor(item.dataType)}`}>
                              {item.dataType === 'entity' ? 'mob' : item.dataType === 'enchantment' ? 'ench' : item.dataType}
                            </span>
                          )}
                        </div>
                        </div>
                      </div>
                    </td>
                    {/* Identifier — its own column from `sm` up; below that it sits under the name */}
                    <td className="hidden sm:table-cell px-3 py-2">
                      {renderIdPill(item)}
                    </td>
                    {/* Type */}
                    {dataType === 'all' && (
                      <td className="hidden sm:table-cell px-3 py-2 text-right">
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${getTypeColor(item.dataType)}`}>
                          {item.dataType === 'entity' ? 'mob' : item.dataType === 'enchantment' ? 'ench' : item.dataType}
                        </span>
                      </td>
                    )}
                    {/* Favorite star */}
                    {showFavorites && (
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!user) {
                              setShowAuthPrompt(true);
                              return;
                            }
                            toggleFavorite({
                              fullId: item.fullId,
                              dataType: item.dataType,
                              displayName: item.displayName,
                              gameVersion: version,
                            });
                          }}
                          className={`p-1 rounded transition-colors ${
                            isFavorited(item.fullId, item.dataType)
                              ? 'text-amber-400 hover:text-amber-300'
                              : 'text-zinc-500 hover:text-zinc-400'
                          }`}
                          title={isFavorited(item.fullId, item.dataType) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <svg className="w-4 h-4" fill={isFavorited(item.fullId, item.dataType) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </button>
                      </td>
                    )}

                  </tr>
                  {/* Expanded detail panel */}
                  {isExpanded && (
                  <tr className={isExpanded ? 'bg-zinc-800/70' : ''}>
                    <td className="px-3 pb-3" colSpan={(dataType === 'all' ? 4 : 3)}>
                      <div className="ml-6 mr-2 p-4 bg-zinc-900/80 border border-zinc-700/50 rounded-lg space-y-2.5" onClick={(e) => e.stopPropagation()}>
                          {/* 1. Identifier */}
                          <DetailRow label="Identifier">
                            <CopyBtn copiedId={copiedId} copyToClipboard={copyToClipboard} text={item.fullId} label="identifier" />
                          </DetailRow>

                          {/* 2. /give command — only where the item registry has this id */}
                          {item.givable && (
                            <DetailRow label="/give command">
                              <CopyBtn copiedId={copiedId} copyToClipboard={copyToClipboard} text={`/give @p ${item.fullId}`} label="give command" />
                            </DetailRow>
                          )}
                          {/* Say why it is missing, rather than leaving a gap that reads as a bug. */}
                          {!item.givable && item.dataType === 'block' && (
                            <DetailRow label="/give command">
                              <span className="text-zinc-400">No item form — this block can only be placed, not given.</span>
                            </DetailRow>
                          )}

                          {/* 3. /setblock command (blocks only) */}
                          {(item.dataType === 'block' || item.blockId) && (
                            <DetailRow label="/setblock command">
                              <CopyBtn copiedId={copiedId} copyToClipboard={copyToClipboard} text={`/setblock ~ ~ ~ ${item.blockId || item.fullId}`} label="setblock command" />
                            </DetailRow>
                          )}

                          {/* 4. Namespace / Mod */}
                          <DetailRow label="Namespace">
                            {item.namespace && item.namespace !== 'minecraft' ? (
                              <div className="flex items-center gap-2">
                                <span className="text-emerald-400">{item.modDisplayName || item.namespace}</span>
                                <span className="text-zinc-500">({item.namespace})</span>
                              </div>
                            ) : (
                              <span className="text-zinc-400">minecraft (Vanilla)</span>
                            )}
                          </DetailRow>
                          {item.namespace && item.namespace !== 'minecraft' && (() => {
                            const links = getModLinks(item.namespace!);
                            if (!links) return null;
                            return (
                              <DetailRow label="Website">
                                <div className="flex items-center gap-3">
                                  <a
                                    href={links.modrinth}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-green-400 hover:text-green-300 hover:underline inline-flex items-center gap-1"
                                  >
                                    Modrinth
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                  <a
                                    href={links.curseforge}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-orange-400 hover:text-orange-300 hover:underline inline-flex items-center gap-1"
                                  >
                                    CurseForge
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                </div>
                              </DetailRow>
                            );
                          })()}

                          {/* 7. Stack size & Durability */}
                          {item.stackSize != null && (
                            <DetailRow label="Stack Size">
                              <span>{item.stackSize}</span>
                            </DetailRow>
                          )}
                          {item.maxDurability != null && item.maxDurability > 0 && (
                            <DetailRow label="Durability">
                              <span>{fmtNum(item.maxDurability)}</span>
                            </DetailRow>
                          )}

                          {/* Rarity */}
                          {item.rarity && item.rarity !== 'common' && (
                            <DetailRow label="Rarity">
                              <span className={`capitalize ${getRarityColor(item.rarity)}`}>{item.rarity}</span>
                            </DetailRow>
                          )}

                          {/* Damage resistant */}
                          {item.damageResistant && (
                            <DetailRow label="Fire/Lava Proof">
                              <span className="text-orange-400">Yes</span>
                            </DetailRow>
                          )}

                          {/* 8. Food stats */}
                          {item.food && (
                            <>
                              <DetailRow label="Hunger">
                                <span>{item.food.foodPoints} points</span>
                              </DetailRow>
                              <DetailRow label="Hunger Saturation">
                                <span>{item.food.saturation}{typeof item.food.saturationRatio === 'number' && ` (${item.food.saturationRatio.toFixed(1)}x ratio)`}</span>
                              </DetailRow>
                              {item.food.canAlwaysEat && (
                                <DetailRow label="Always Edible">
                                  <span className="text-emerald-400">Yes</span>
                                </DetailRow>
                              )}
                            </>
                          )}

                          {/* 9. Block properties */}
                          {(item.dataType === 'block' || item.blockId) && (
                            <>
                              {item.hardness != null && (
                                <DetailRow label="Hardness">
                                  <span>{item.hardness === -1 ? 'Unbreakable' : item.hardness}</span>
                                </DetailRow>
                              )}
                              {item.blastResistance != null && (
                                <DetailRow label="Blast Resistance">
                                  <span>{item.blastResistance >= 3600000 ? 'Indestructible' : item.blastResistance}</span>
                                </DetailRow>
                              )}
                              {item.emitLight != null && item.emitLight > 0 && (
                                <DetailRow label="Light Level">
                                  <span>{item.emitLight}</span>
                                </DetailRow>
                              )}
                              {item.transparent != null && (
                                <DetailRow label="Transparent">
                                  <span>{item.transparent ? 'Yes' : 'No'}</span>
                                </DetailRow>
                              )}
                              {item.requiresTool != null && (
                                <DetailRow label="Requires Tool">
                                  <span>{item.requiresTool ? 'Yes' : 'No'}</span>
                                </DetailRow>
                              )}
                              {item.material && item.material !== 'default' && (
                                <DetailRow label="Mining Tool">
                                  <span className="capitalize">{item.material.replace('mineable/', '')}</span>
                                </DetailRow>
                              )}
                            </>
                          )}

                          {/* 10. Enchantments */}
                          {item.enchantCategories && item.enchantCategories.length > 0 && (
                            <DetailRow label="Enchantments">
                              <div className="flex flex-wrap gap-1">
                                {item.enchantCategories.map(e => (
                                  <span key={e} className="text-xs bg-purple-500/15 text-purple-300 px-1.5 py-0.5 rounded capitalize">
                                    {e.replace(/_/g, ' ')}
                                  </span>
                                ))}
                              </div>
                            </DetailRow>
                          )}

                          {/* 11. Repair with */}
                          {item.repairWith && item.repairWith.length > 0 && (
                            <DetailRow label="Repair With">
                              <div className="flex flex-wrap gap-1">
                                {item.repairWith.map(r => (
                                  <span key={r} className="text-xs bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded capitalize">
                                    {r.replace(/_/g, ' ')}
                                  </span>
                                ))}
                              </div>
                            </DetailRow>
                          )}

                          {/* Tags */}
                          {(() => {
                            const itemType = item.dataType === 'entity' ? 'entity' : item.dataType === 'block' ? 'block' : 'item';
                            const tags = getItemTags(item.stringId, itemType);
                            if (tags.length === 0) return null;
                            return (
                              <DetailRow label="Tags">
                                <div className="flex flex-wrap gap-1">
                                  {tags.map(tag => (
                                    <span key={tag} className="text-[10px] bg-zinc-700/80 text-zinc-400 px-1.5 py-0.5 rounded font-mono">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </DetailRow>
                            );
                          })()}

                          {/* Enchantment details */}
                          {item.dataType === 'enchantment' && (
                            <>
                              {item.maxLevel != null && (
                                <DetailRow label="Max Level">
                                  <span>{item.maxLevel}</span>
                                </DetailRow>
                              )}
                              {item.enchantCategory && (
                                <DetailRow label="Category">
                                  <span className="capitalize">{item.enchantCategory.replace(/_/g, ' ')}</span>
                                </DetailRow>
                              )}
                              {item.enchantRarity && (
                                <DetailRow label="Rarity">
                                  <span className={`capitalize ${getRarityColor(item.enchantRarity)}`}>{item.enchantRarity.replace(/_/g, ' ')}</span>
                                </DetailRow>
                              )}
                              {item.treasureOnly && (
                                <DetailRow label="Treasure Only">
                                  <span className="text-amber-400">Yes</span>
                                </DetailRow>
                              )}
                              {item.curse && (
                                <DetailRow label="Curse">
                                  <span className="text-red-400">Yes</span>
                                </DetailRow>
                              )}
                              {item.appliesTo && item.appliesTo.length > 0 && (
                                <DetailRow label="Applies To">
                                  <div className="flex flex-wrap gap-1">
                                    {item.appliesTo.slice(0, 20).map(id => (
                                      <span key={id} className="text-xs bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded">
                                        {id.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                    {item.appliesTo.length > 20 && (
                                      <span className="text-xs text-zinc-500">+{item.appliesTo.length - 20} more</span>
                                    )}
                                  </div>
                                </DetailRow>
                              )}
                              {item.exclude && item.exclude.length > 0 && (
                                <DetailRow label="Incompatible With">
                                  <div className="flex flex-wrap gap-1">
                                    {item.exclude.map(id => (
                                      <span key={id} className="text-xs bg-red-500/15 text-red-300 px-1.5 py-0.5 rounded">
                                        {id.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                  </div>
                                </DetailRow>
                              )}
                            </>
                          )}

                          {/* Effect details */}
                          {item.dataType === 'effect' && (
                            <>
                              <DetailRow label="Type">
                                <span className={
                                  item.effectType === 'good' ? 'text-emerald-400' :
                                  item.effectType === 'bad' ? 'text-red-400' :
                                  'text-zinc-400'
                                }>
                                  {item.effectType === 'good' ? 'Beneficial' : item.effectType === 'bad' ? 'Harmful' : 'Neutral'}
                                </span>
                              </DetailRow>
                              {item.effectColor && (
                                <DetailRow label="Particle Color">
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded border border-zinc-600" style={{ backgroundColor: item.effectColor }} />
                                    <span className="text-xs font-mono text-zinc-400">{item.effectColor}</span>
                                  </div>
                                </DetailRow>
                              )}
                              {item.instant && (
                                <DetailRow label="Instant">
                                  <span className="text-cyan-400">Yes</span>
                                </DetailRow>
                              )}
                              <DetailRow label="/effect command">
                                <CopyBtn copiedId={copiedId} copyToClipboard={copyToClipboard} text={`/effect give @p ${item.fullId}`} label="effect command" />
                              </DetailRow>
                            </>
                          )}

                          {/* 14. Recipes — link to recipe viewer */}
                          {item.dataType !== 'entity' && (() => {
                            const { obtainCount, usedInCount } = getRecipeSummary(item.stringId);
                            if (obtainCount === 0 && usedInCount === 0) return null;

                            // Get recipe type breakdown for "how to obtain"
                            const obtainRecipes = recipeIndexes?.forward.get(item.stringId) ?? [];
                            const obtainTypes = [...new Set(obtainRecipes.map(r => r.type))];

                            return (
                              <DetailRow label="Recipes">
                                <div className="flex flex-col gap-1.5">
                                  {obtainCount > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-zinc-400">Obtained by:</span>
                                      {obtainTypes.map(t => (
                                        <span key={t} className="text-xs bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded">
                                          {formatRecipeType(t)}
                                        </span>
                                      ))}
                                      <span className="text-zinc-500">({obtainCount} recipe{obtainCount !== 1 ? 's' : ''})</span>
                                    </div>
                                  )}
                                  {usedInCount > 0 && (
                                    <span className="text-zinc-400">
                                      Used in {usedInCount} recipe{usedInCount !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                  <Link
                                    href={`/tools/recipe-viewer?item=${encodeURIComponent(item.stringId)}`}
                                    className="text-xs text-emerald-400 hover:text-emerald-300 hover:underline inline-flex items-center gap-1 w-fit"
                                  >
                                    View in Recipe Viewer
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </Link>
                                </div>
                              </DetailRow>
                            );
                          })()}
                      </div>
                    </td>
                  </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderPagination('bottom')}

          {filteredData.length === 0 && (
            <div className="p-12 text-center">
              <svg className="w-12 h-12 mx-auto text-zinc-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-zinc-500">No entries found matching your criteria</p>
            </div>
          )}
        </div>
      </div>

      <AuthPromptModal
        open={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        message="Sign in to favorite items and access them quickly from your account."
      />
    </div>
  );
}

// --- Extracted sub-components (module scope to avoid re-creation per render) ---

function SortIndicator({ field, sortField, sortDirection }: { field: SortField; sortField: SortField; sortDirection: SortDirection }) {
  if (sortField !== field) return null;
  return (
    <span className="ml-1" aria-hidden="true">
      {sortDirection === 'asc' ? '\u25B2' : '\u25BC'}
    </span>
  );
}

const CopyBtn = React.memo(function CopyBtn({
  text,
  label,
  copiedId,
  copyToClipboard,
}: {
  text: string;
  label?: string;
  copiedId: string | null;
  copyToClipboard: (text: string, id: string) => void;
}) {
  const key = `copy-${text}`;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); copyToClipboard(text, key); }}
      className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded transition-colors ${
        copiedId === key
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white'
      }`}
      title={copiedId === key ? 'Copied!' : `Copy ${label || text}`}
    >
      <span className="truncate max-w-[350px]">{text}</span>
      {copiedId === key ? (
        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3 h-3 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
});

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-baseline">
      <span className="text-xs text-zinc-500 w-32 shrink-0 text-right">{label}</span>
      <div className="text-sm text-zinc-200">{children}</div>
    </div>
  );
}
